class AccordionElement extends window.BAO.DetailsDisclosure {
  static get requiredElements () {
    return ['details', 'summary', 'content']
  }

  constructor () {
    super()
    this.animation = null
    this.isClosing = false
    this.isExpanding = false
    this.handleExpandByDefault()
    this.handleExpandAtViewport()
  }

  setupListeners () {
    super.setupListeners()

    this.onClick = this.onClick.bind(this)
    this.onFocusOut = this.onFocusOut.bind(this)
    this.onTriggerClick = this.onTriggerClick.bind(this)
    this.handleScrollToTriggerClick = this.handleScrollToTriggerClick.bind(this)

    this.listeners.add(this.summaryEl, 'click', this.onClick)
    this.listeners.add(this.els.details.element, 'focusout', this.onFocusOut)

    if (this.externalTriggerEls) {
      this.externalTriggerEls.forEach(externalTriggerEl =>
        this.listeners.add(externalTriggerEl, 'click', this.onTriggerClick),
      )
    }

    if (this.scrollToTriggerEls) {
      this.scrollToTriggerEls.forEach(scrollToTriggerEl =>
        this.listeners.add(
          scrollToTriggerEl,
          'click',
          this.handleScrollToTriggerClick,
        ),
      )
    }

    if (this.getAttribute(`data-expand-at`)) {
      this.debouncedOnResize = window.BAO.Utils.debounce(event => {
        this.onResize(event)
      }, 250)

      this.listeners.add(window, 'resize', this.debouncedOnResize.bind(this))
    }
  }

  onTriggerClick (e) {
    const externalTriggerEl = e.target.closest(this.externalTriggerSelector)
    if (
      externalTriggerEl?.dataset?.allowClose === 'false' &&
      this.detailsEl.open.toString() === 'true'
    ) {
      return
    }
    this.onClick(e)
  }

  handleScrollToTriggerClick (e) {
    e.preventDefault()
    const scrollToTriggerEl = e.target.closest(
      this.externalEls?.scrollToTrigger?.selector,
    )
    if (!scrollToTriggerEl) return
    const href = scrollToTriggerEl.getAttribute('href')
    const elementToScrollTo = href
      ? window.BAO.Utils.getElement(href, { context: document })
      : null
    if (!elementToScrollTo) return
    const rect = elementToScrollTo.getBoundingClientRect()
    const announcementBar = document.querySelector('header-announcement-bar')
    const announcementBarHeight =
      announcementBar !== null ? announcementBar.offsetHeight : 0
    const headerHeight = document.querySelector('.hd-Header').offsetHeight
    const combinedHeight = announcementBarHeight + headerHeight

    window.BAO.Utils.scrollToY(rect.top + window.scrollY - combinedHeight)

    if (
      href === this.hashedId &&
      this?.detailsEl?.open?.toString() !== `true`
    ) {
      this.summaryEl.click()
    }
  }

  onClick (e) {
    e.preventDefault()
    this.detailsEl.style.overflow = 'hidden'
    if (this.isClosing || !this.detailsEl.open) {
      this.open()
    } else if (this.isExpanding || this.detailsEl.open) {
      this.shrink()
    }
  }

  onFocusOut (e) {
    e.preventDefault()
    if (!this.autoClose || this.expandAtViewport) return
    this.detailsEl.style.overflow = 'hidden'
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.shrink()
    })
  }

  onResize () {
    this.handleExpandAtViewport()
  }

  handleExpandByDefault () {
    this.detailsEl.open = this.expandByDefault
    this.summaryEl.setAttribute('aria-expanded', !!this.expandByDefault)
  }

  handleExpandAtViewport () {
    if (this.expandByDefault || this.detailsEl.open) return
    this.detailsEl.open = this.expandAtViewport
    this.summaryEl.setAttribute('aria-expanded', !!this.expandAtViewport)
  }

  open () {
    this.detailsEl.style.height = `${this.detailsEl.offsetHeight}px`
    this.detailsEl.open = true
    window.requestAnimationFrame(() => this.expand())
  }

  shrink () {
    this.isClosing = true
    const startHeight = `${this.detailsEl.offsetHeight}px`
    const endHeight = `${this.summaryEl.offsetHeight}px`
    this.beforeAnimation(startHeight, endHeight)
    this.animate(startHeight, endHeight)
    this.animation.onfinish = () => this.onAnimationFinish(false)
    this.animation.oncancel = () => (this.isClosing = false)
    this.summaryEl.setAttribute('aria-expanded', 'false')
    this.contentEl.setAttribute('aria-hidden', 'true')
  }

  expand () {
    this.isExpanding = true
    const startHeight = `${this.detailsEl.offsetHeight}px`
    const endHeight = `${this.summaryEl.offsetHeight + this.contentHeight}px`
    this.beforeAnimation(startHeight, endHeight)
    this.animate(startHeight, endHeight)
    this.animation.onfinish = () => this.onAnimationFinish(true)
    this.animation.oncancel = () => (this.isExpanding = false)
    this.summaryEl.setAttribute('aria-expanded', 'true')
    this.contentEl.setAttribute('aria-hidden', 'false')
  }

  beforeAnimation (startHeight, endHeight) {
    this.dispatchEvent(
      new CustomEvent('BEFORE_ACCORDION_ANIMATION', {
        bubbles: true,
        detail: { element: this, startHeight, endHeight },
      }),
    )
  }

  animate (startHeight, endHeight) {
    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.detailsEl.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: 400,
        easing: 'ease-in-out',
      },
    )
  }

  onAnimationFinish (open) {
    this.detailsEl.open = open
    this.animation = null
    this.isClosing = false
    this.isExpanding = false
    this.detailsEl.style.height = this.detailsEl.style.overflow = ''
    this.dispatchEvent(
      new CustomEvent('AFTER_ACCORDION_ANIMATION', {
        bubbles: true,
        detail: { element: this, open },
      }),
    )
  }

  get summaryEl () {
    return this.els.summary.element
  }

  get detailsEl () {
    return this.els.details.element
  }

  get contentEl () {
    return this.els.content.element
  }

  get externalTriggerEls () {
    // return this.externalEls?.trigger?.elements
    return document.querySelectorAll(this.externalTriggerSelector)
  }

  get scrollToTriggerEls () {
    return this.externalEls?.scrollToTrigger?.elements || []
  }

  get externalTriggerSelector () {
    return `[data-${this.identifier}-external-el="trigger"]`
  }

  get contentHeight () {
    return this.contentEl ? this.contentEl.offsetHeight : 0
  }

  get autoClose () {
    return this.dataset?.autoClose !== 'false'
  }

  get expandAt () {
    return this.dataset?.expandAt ? parseInt(this.dataset.expandAt, 10) : null
  }

  get expandAtViewport () {
    return this.expandAt && this.expandAt <= this.viewportWidth
  }

  get expandByDefault () {
    return this.dataset?.expandByDefault === `true`
  }

  get viewportWidth () {
    return window.innerWidth
  }

  get hashedId () {
    return `#${this.id}`
  }
}

if (!customElements.get('accordion-element')) {
  customElements.define('accordion-element', AccordionElement)
}
